/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useCallback, useState } from 'react';
import { useForm, type FormContext } from '../../helpers/parameter-renderer/hooks/useForm';
import { PROFILE_FORM_SCHEMA } from './schema';
import { authApi } from '../../service/api/auth/Api';
import { showValidationErrorToasts, validateProfileForm } from './validation';
import { buildProfileFormData, transformEducationFields } from './formData';

// --- Types ---

export type UseProfileFormOptions = {
  onSuccess?: () => void;
  initialData?: Partial<Record<string, unknown>>;
};

export type UseProfileFormReturn = {
  formCtx: FormContext<Record<string, unknown>>;
  handleSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  submitError: string | null;
  clearError: () => void;
  setForm: (data: Partial<Record<string, unknown>>) => void;
};

// --- Hook Implementation ---

/**
 * Custom hook for managing profile form state, validation, and submission
 *
 * @param options - Configuration options for the hook
 * @param options.onSuccess - Callback function called after successful form submission
 * @param options.initialData - Optional initial form data to populate the form
 * @returns Form context, submit handler, and state management functions
 */
export function useProfileForm(options?: UseProfileFormOptions): UseProfileFormReturn {
  const { onSuccess, initialData } = options || {};
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [updateWebProfile] = authApi.useUpdateWebProfileMutation();

  const { ctx, setForm } = useForm({
    parameters: PROFILE_FORM_SCHEMA,
    initialState: initialData,
  });

  const clearError = useCallback(() => {
    setSubmitError(null);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const data = ctx.form as Record<string, unknown>;

      // 1. Custom Client-side Validation
      const validationResult = validateProfileForm(data);

      if (!validationResult.isValid) {
        // Show toasts
        showValidationErrorToasts(validationResult.errors);

        // Highlight fields in red
        Object.entries(validationResult.errors).forEach(([fieldId, errorMessage]) => {
          ctx.setError(fieldId, errorMessage);
        });

        return;
      }

      // 2. Proceed with submission if valid
      void (async () => {
        setIsSubmitting(true);
        setSubmitError(null);

        try {
          // Transform education fields
          const transformedData = transformEducationFields(data);

          // Build FormData
          const formData = buildProfileFormData(transformedData);

          // Submit to API
          await updateWebProfile({ profile: formData }).unwrap();

          // Call success callback if provided
          if (onSuccess) {
            onSuccess();
          }
        } catch (err) {
          console.error('Error submitting profile:', err);
          setSubmitError(
            err && typeof err === 'object' && 'data' in err && typeof err.data === 'string'
              ? err.data
              : 'Failed to submit profile. Please try again.',
          );
        } finally {
          setIsSubmitting(false);
        }
      })();
    },
    [ctx, updateWebProfile, onSuccess],
  );

  return {
    formCtx: ctx,
    handleSubmit,
    isSubmitting,
    submitError,
    clearError,
    setForm,
  };
}
